// The query layer: the UI asks the graph for connections and receives GROUPED,
// trust-ordered results - never a raw array of links.
//
// Group order (the trusted layer first, the exploratory layer last and OFF by
// default):
//   1. verified       - structural commentary, verified/high citations, quotes
//   2. text_family    - same base text, translations, variants
//   3. quoted_by      - downstream sources that quote this
//   4. related        - cross-references, parallels, shared phrases
//   5. explore_deeper - thematic + AI suggestions (only when includeSuggested)
//
// Edges that fail validation, point at invalid/unknown passages, or were
// rejected NEVER appear in groups; they are tallied in hiddenCounts so the UI
// can say "N hidden" instead of silently truncating.

import { validateSourceEdge, validatePassage } from "./sourceValidation.ts";
import type {
  Confidence,
  Evidence,
  Passage,
  SourceEdge,
  SourceGraphQuery,
  SourceGraphResult,
  TextVersion,
  Topic,
  Work,
} from "./sourceGraph.ts";

// ---------------------------------------------------------------------------
// In-memory store (the current backing for fixtures / the integrity report /
// the query layer; a future provider import writes into the same shape).
// ---------------------------------------------------------------------------

export interface SourceGraphStore {
  works: Map<string, Work>;
  versions: Map<string, TextVersion>;
  passages: Map<string, Passage>;
  edges: Map<string, SourceEdge>;
  evidence: Map<string, Evidence>;
  topics: Map<string, Topic>;
}

export interface StoreInit {
  works?: Work[];
  versions?: TextVersion[];
  passages?: Passage[];
  edges?: SourceEdge[];
  evidence?: Evidence[];
  topics?: Topic[];
}

export function makeStore(init: StoreInit = {}): SourceGraphStore {
  const byId = <T extends { id: string }>(arr: T[] | undefined): Map<string, T> =>
    new Map((arr ?? []).map((x) => [x.id, x]));
  return {
    works: byId(init.works),
    versions: byId(init.versions),
    passages: byId(init.passages),
    edges: byId(init.edges),
    evidence: byId(init.evidence),
    topics: byId(init.topics),
  };
}

export function evidenceForEdge(store: SourceGraphStore, edge: SourceEdge): Evidence[] {
  return edge.evidenceIds
    .map((id) => store.evidence.get(id))
    .filter((e): e is Evidence => !!e);
}

// ---------------------------------------------------------------------------
// Grouping
// ---------------------------------------------------------------------------

type GroupId = "verified" | "text_family" | "quoted_by" | "related" | "explore_deeper";

// Exported as the ONE source of truth for group titles - the Explore panel
// (navigator.ts) renders these same strings, so the two layers cannot drift.
// The first group is titled "Trusted connections", NOT "Verified connections":
// it holds the trusted Torah graph (structural commentary, text-confirmed
// citations, provider links), whose members may badge verified OR probable -
// a "Verified" title over a probable-badged card would overstate trust.
export const GROUP_META: Record<GroupId, { title: string; description: string }> = {
  verified: {
    title: "Trusted connections",
    description: "Structural commentary, text-confirmed citations and quoted sources",
  },
  text_family: {
    title: "Text family",
    description: "Same base text, translations and textual variants",
  },
  quoted_by: {
    title: "Quoted by",
    description: "Later sources that quote this",
  },
  related: {
    title: "Probably related",
    description: "Cross-references, parallel sources and shared phrases",
  },
  explore_deeper: {
    title: "Explore deeper",
    description: "Thematic and AI suggestions - leads, not verified citations",
  },
};

const CONF_RANK: Record<Confidence, number> = {
  verified: 0,
  high: 1,
  medium: 2,
  low: 3,
  suggested: 4,
  rejected: 5,
};

// Is this edge in the EXPLORATORY layer (quiet, off by default)?
export function isSuggestedEdge(edge: SourceEdge): boolean {
  return (
    edge.confidence === "suggested" ||
    edge.provenance.origin === "ai_suggested" ||
    edge.edgeClass === "ai" ||
    edge.edgeClass === "topic" ||
    edge.relationshipType === "same_topic" ||
    edge.relationshipType === "ai_suggested" ||
    edge.relationshipType === "user_created_link"
  );
}

function groupFor(edge: SourceEdge): GroupId {
  if (isSuggestedEdge(edge)) return "explore_deeper";
  switch (edge.relationshipType) {
    case "comments_on":
    case "supercomments_on":
    case "explains":
    case "translation_of":
      return edge.relationshipType === "translation_of" ? "text_family" : "verified";
    case "cites":
    case "quotes":
      return edge.confidence === "verified" || edge.confidence === "high" ? "verified" : "related";
    case "is_quoted_by":
      return "quoted_by";
    case "same_base_text":
    case "variant_of":
    case "manuscript_variant":
      return "text_family";
    default:
      return "related";
  }
}

const LENS_RELS: Record<string, SourceEdge["relationshipType"][]> = {
  commentary: ["comments_on", "supercomments_on", "explains"],
  citations: ["cites", "quotes"],
  quoted_by: ["is_quoted_by"],
  halacha: ["halachic_source_for", "halachic_ruling_based_on"],
  language: ["same_phrase"],
  theme: ["same_topic"],
};

// ---------------------------------------------------------------------------
// The query
// ---------------------------------------------------------------------------

// @scaffold - this is the source-graph trust layer's spec + the DEV integrity
// report's backbone, but it has NO live UI caller yet: the navigator's Explore
// panel adapts buckets through sourceGraphAdapter.edgeFromBucket directly. Keep it
// (type-checked + test-covered so it cannot rot), but do not assume it is on the
// hot path. See sourceGraph.ts "WIRING STATUS".
export function querySourceGraph(
  store: SourceGraphStore,
  query: SourceGraphQuery,
): SourceGraphResult {
  const current = store.passages.get(query.currentPassageId);
  if (!current) {
    // Fail loud: querying an unknown passage is a caller bug, never something
    // to paper over with an empty result.
    throw new Error(`querySourceGraph: unknown passage "${query.currentPassageId}"`);
  }

  const hidden = { suggested: 0, lowConfidence: 0, unsupported: 0, truncated: 0 };
  const grouped = new Map<GroupId, SourceEdge[]>();

  for (const edge of store.edges.values()) {
    // Only edges touching the current passage.
    if (edge.fromPassageId !== query.currentPassageId && edge.toPassageId !== query.currentPassageId)
      continue;

    if (edge.visibility !== "public") continue; // hidden/admin/experimental
    if (edge.confidence === "rejected" || edge.reviewed?.status === "rejected") continue;

    // Structural soundness: a broken edge or an edge into an invalid/unknown
    // passage must not render as a normal link.
    if (!validateSourceEdge(edge).valid) {
      hidden.unsupported++;
      continue;
    }
    const otherId = edge.fromPassageId === query.currentPassageId ? edge.toPassageId : edge.fromPassageId;
    const other = store.passages.get(otherId);
    if (!other || !validatePassage(other).valid) {
      hidden.unsupported++;
      continue;
    }
    // Evidence must RESOLVE, not merely be claimed - an edge whose evidence
    // ids all dangle is as unsupported as one with none.
    if (evidenceForEdge(store, edge).length === 0) {
      hidden.unsupported++;
      continue;
    }

    // The exploratory layer is opt-in (the "theme" lens opts in implicitly).
    const suggested = isSuggestedEdge(edge);
    if (suggested && !query.includeSuggested && query.lens !== "theme") {
      hidden.suggested++;
      continue;
    }

    // Explicit filters.
    if (query.includeRelationshipTypes && !query.includeRelationshipTypes.includes(edge.relationshipType)) continue;
    if (query.includeEdgeClasses && !query.includeEdgeClasses.includes(edge.edgeClass)) continue;
    if (query.minStrength && edge.strength < query.minStrength) {
      hidden.lowConfidence++;
      continue;
    }
    if (query.confidence && !query.confidence.includes(edge.confidence)) {
      hidden.lowConfidence++;
      continue;
    }
    if (query.categories) {
      const work = store.works.get(other.workId);
      if (!work || !query.categories.includes(work.category)) continue;
    }
    if (query.lens && query.lens !== "all") {
      if (query.lens === "midrash") {
        const work = store.works.get(other.workId);
        if (work?.category !== "Midrash") continue;
      } else {
        const rels = LENS_RELS[query.lens];
        if (rels && !rels.includes(edge.relationshipType)) {
          if (!(query.lens === "halacha" && edge.edgeClass === "halachic_chain")) continue;
        }
      }
    }

    const gid = groupFor(edge);
    const arr = grouped.get(gid) ?? [];
    arr.push(edge);
    grouped.set(gid, arr);
  }

  const order: GroupId[] = ["verified", "text_family", "quoted_by", "related", "explore_deeper"];
  const groups = order
    .filter((gid) => (grouped.get(gid)?.length ?? 0) > 0)
    .map((gid) => {
      let edges = grouped
        .get(gid)!
        .sort(
          (a, b) =>
            b.strength - a.strength ||
            CONF_RANK[a.confidence] - CONF_RANK[b.confidence] ||
            a.toRef.localeCompare(b.toRef),
        );
      if (query.limit && edges.length > query.limit) {
        // bounded per group; the overflow is reported as truncation, never
        // silent and never mislabeled as low confidence
        hidden.truncated += edges.length - query.limit;
        edges = edges.slice(0, query.limit);
      }
      return { id: gid, title: GROUP_META[gid].title, description: GROUP_META[gid].description, edges };
    });

  const hiddenCounts =
    hidden.suggested || hidden.lowConfidence || hidden.unsupported || hidden.truncated
      ? {
          suggested: hidden.suggested || undefined,
          lowConfidence: hidden.lowConfidence || undefined,
          unsupported: hidden.unsupported || undefined,
          truncated: hidden.truncated || undefined,
        }
      : undefined;

  return { current, groups, hiddenCounts };
}
